import type { FC, ReactNode } from 'react'
import type { GetStaticProps } from 'next'
import type { TeamMember } from '@/types'

import Link from 'next/link'
import { ArrowUpRight } from 'lucide-react'
import { getTeam } from '@/lib/cms'
import { MaskLines, Reveal } from '@/components/motion/reveal'
import { HeroField } from '@/components/motion/hero-field'
import { ScrambleText } from '@/components/motion/scramble-text'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { ToneSection } from '@/components/generals/tone'
import { Cta } from '@/components/generals/cta'
import { Story } from '@/components/landing/about/story'
import { TeamList } from '@/components/landing/about/team-list'
import { Offices } from '@/components/landing/shared/offices'
import { backers } from '@/constants/content'

interface AboutProps {
    team: TeamMember[]
}

const About: FC<AboutProps> = ({ team }): ReactNode => {
    return (
        <Layout tone='void'>
            <Seo
                title='About us'
                description='nSure.ai is the payment fraud prevention provider of choice for gaming, prepaid and gift cards, and crypto. Founded on a gift card platform in 2013, born as nSure.ai in 2019.'
            />

            <ToneSection tone='void' id='hero' className='isolate overflow-hidden px-5 pb-24 pt-32 md:px-8 lg:px-12 lg:pb-36 lg:pt-44'>
                <HeroField />

                <span className='t-label text-tone-muted'>
                    <ScrambleText text='About nSure.ai' delay={200} />
                </span>

                <h1 className='mt-10 font-heading font-semibold uppercase tracking-[-0.04em]'>
                    <MaskLines
                        as='span'
                        immediate
                        delay={0.1}
                        className='block max-w-6xl text-[clamp(2.4rem,5.6vw,5.75rem)] leading-[0.92] wdth-75'
                        lines={['The payment fraud', 'prevention provider', 'of choice for']}
                    />
                    <MaskLines
                        as='span'
                        immediate
                        delay={0.4}
                        className='mt-2 block pl-[8vw] font-serif text-[clamp(2.75rem,7.4vw,7.75rem)] font-normal normal-case italic leading-[0.95] tracking-[-0.02em] text-tone-accent'
                        lines={['Gaming,', 'Prepaid & Gift Cards,', 'and Crypto.']}
                    />
                </h1>

                <div className='mt-20 grid gap-10 lg:mt-28 lg:grid-cols-12'>
                    <Reveal className='t-lead text-tone lg:col-span-6'>
                        nSure.ai turns fraud prevention into a profit driver for crypto exchanges and high-risk digital merchants. Unlike one-size-fits-all fraud tools, we build a dedicated AI model for each merchant trained on their own transaction data, focusing on behavioral patterns, the only signal fraudsters can’t fake.
                    </Reveal>
                    <Reveal className='flex flex-col gap-8 lg:col-span-4 lg:col-start-9' delay={0.1}>
                        <p className='text-tone-muted'>
                            Enjoy zero-risk net incremental profit, up to 98% approval rates, decisions in under 500ms, and a chargeback guarantee backed by full financial liability.
                        </p>
                        <div>
                            <Cta href='/contact' cursor='Say hi'>
                                Get in touch
                            </Cta>
                        </div>
                    </Reveal>
                </div>
            </ToneSection>

            <Story />

            <TeamList team={team} />

            <ToneSection tone='ink' id='backing' className='px-5 py-28 md:px-8 lg:px-12 lg:py-36'>
                <div className='grid gap-12 lg:grid-cols-12'>
                    <div className='flex flex-col gap-8 lg:col-span-5'>
                        <span className='t-label text-tone-muted'>
                            Backing
                        </span>
                        <MaskLines
                            as='h2'
                            className='font-heading t-h1 font-semibold uppercase wdth-75'
                            lines={['We’re not the only', 'ones betting on', 'our success.']}
                        />
                        <Reveal className='text-tone-muted'>
                            nSure.ai’s payment fraud prevention solution is backed by Phoenix and Kamet, while Lloyds has joined them with a stop-loss policy.
                        </Reveal>
                        <Reveal>
                            <Link href='/#who-insures' className='group inline-flex items-center gap-2 font-semibold'>
                                <span className='link-line'>
                                    Who insures nSure.ai?
                                </span>
                                <ArrowUpRight className='size-4 transition-transform duration-500 group-hover:rotate-45' aria-hidden />
                            </Link>
                        </Reveal>
                    </div>

                    <ul className='flex flex-col lg:col-span-6 lg:col-start-7'>
                        {backers.map(backer => (
                            <li key={backer.name} className='flex items-end justify-between gap-6 border-t border-tone-line py-6 last:border-b'>
                                <span className='font-heading text-[clamp(3rem,7vw,6.5rem)] font-semibold uppercase leading-[0.85] tracking-[-0.04em] wdth-100'>
                                    {backer.name}
                                </span>
                                <span className='t-label pb-2 text-tone-accent'>
                                    {backer.role}
                                </span>
                            </li>
                        ))}
                    </ul>
                </div>
            </ToneSection>

            <Offices tone='lilac' />
        </Layout>
    )
}

export const getStaticProps: GetStaticProps<AboutProps> = async () => {
    try {
        const team = await getTeam()

        return { props: { team }, revalidate: 60 }
    }
    catch (error) {
        console.error('Team could not be loaded:', error instanceof Error ? error.message : error)

        return { props: { team: [] }, revalidate: 30 }
    }
}

export default About
